// apps/web/src/lib/progress/webProgressStore.ts
// ─────────────────────────────────────────────────────────────────────────
// WEB PROGRESS STORE — localStorage implementation of the core ProgressStore.
//
// The historical key prefixes (nis.lessonProgress. / nis.lesson.) are owned by
// core (ProgressStore.ts) and PRESERVED here byte-for-byte, because:
//   • localExport.ts groups exported data by these prefixes, and
//   • migrateLocalStorage.ts reads the legacy boolean to seed records.
// Changing them silently breaks export + migration, so don't.
//
// Every method is try/wrapped and never throws — progress writes must not be
// able to crash a render or a reward dispatch. This matches the old
// lessonProgress.ts / useProgress.ts behavior exactly.
// ─────────────────────────────────────────────────────────────────────────

import type { ProgressStore } from '@nisk/core/progress';

export const webProgressStore: ProgressStore = {
	get(key: string): string | null {
		try {
			return localStorage.getItem(key);
		} catch {
			return null;
		}
	},

	set(key: string, value: string): void {
		try {
			localStorage.setItem(key, value);
		} catch {
			/* quota / private-mode — best effort */
		}
	},

	remove(key: string): void {
		try {
			localStorage.removeItem(key);
		} catch {
			/* noop */
		}
	},

	keys(prefix: string): string[] {
		try {
			const out: string[] = [];
			for (let i = 0; i < localStorage.length; i++) {
				const k = localStorage.key(i);
				if (k && k.startsWith(prefix)) out.push(k);
			}
			return out;
		} catch {
			return [];
		}
	},
};
